import { Component, inject, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EditorService } from '../../../services/editor/editor.service';
import { DiagramConfigurationService } from '../../../services/diagram/diagram-configuration.service';
import { ParticipantDrawerService } from '../../../services/diagram/participant-drawer.service';
import { LifelineDrawerService } from '../../../services/diagram/lifeline-drawer.service';
import { MessageDrawerService } from '../../../services/diagram/message-drawer.service';

import { CANVAS_FONT, CANVAS_TEXT_ALIGN, DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH, MARGIN_BOTTOM, TOP_X, TOP_Y } from '../constants';

import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-diagram-canvas',
  standalone: true,
  imports: [CommonModule],
  template: `<canvas #canvas class="diagram-background"></canvas>`,
  styleUrls: ['./diagram-canvas.component.scss'],
})
export class DiagramCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private _editorService = inject(EditorService);
  private _participantDrawer = inject(ParticipantDrawerService);
  private _lifelineDrawer = inject(LifelineDrawerService);
  private _messageDrawer = inject(MessageDrawerService);
  private _diagramConfigurationService = inject(DiagramConfigurationService);

  private _ctx!: CanvasRenderingContext2D;
  private _destroy$ = new Subject<void>();

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this._ctx = canvas.getContext('2d')!;

    fromEvent(window, 'resize')
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.resizeCanvas());

    this._editorService.diagramText$.pipe(takeUntil(this._destroy$)).subscribe(text => this.drawDiagram(text));

    this.resizeCanvas();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = canvas.parentElement?.clientWidth || DEFAULT_CANVAS_WIDTH;
    canvas.height = canvas.parentElement?.clientHeight || DEFAULT_CANVAS_HEIGHT;

    const currentText = this._editorService.getCurrentDiagramText();
    if (currentText) {
      this.drawDiagram(currentText);
    }
  }

  drawDiagram(text: string): void {
    const ctx = this._ctx;
    const canvas = this.canvasRef.nativeElement;

    const computedStyle = getComputedStyle(document.body);
    const backgroundColor = computedStyle.getPropertyValue('--white-color').trim();
    const drawColor = computedStyle.getPropertyValue('--black-color').trim();

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(TOP_X, TOP_Y, canvas.width, canvas.height);

    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
    ctx.font = CANVAS_FONT;
    ctx.textAlign = CANVAS_TEXT_ALIGN;

    const lines = this._diagramConfigurationService.parseEditorContent(text);

    const participants = this._diagramConfigurationService.extractParticipants(lines);
    this._diagramConfigurationService.optimiseCanvasWidth(participants, canvas, ctx);
    this._diagramConfigurationService.optimiseCanvasHeight(lines, canvas);
    this._diagramConfigurationService.assignParticipantPositions(participants, ctx);

    this._participantDrawer.drawAll(ctx, participants);
    this._lifelineDrawer.drawLifeLines(ctx, participants, canvas.height - MARGIN_BOTTOM);
    this._messageDrawer.drawAll(lines, ctx, participants);
  }
}
